// src/modules/treasury/api.js
// API client for Treasury Management System.
// All endpoints under /api/v1/treasury/

import API from '../../services/api';
import { API_ENDPOINTS } from './constants/treasuryConstants';

// --- Cash Ledger ---
export const fetchCashLedger = (params) =>
  API.get(API_ENDPOINTS.CASH_LEDGER, { params });

export const fetchCashLedgerBalance = (params) =>
  API.get(API_ENDPOINTS.CASH_LEDGER_BALANCE, { params });

export const fetchCombinedLedger = (params) =>
  API.get(API_ENDPOINTS.COMBINED_LEDGER, { params });

// --- Payment Requests ---
export const fetchPaymentRequests = (params) =>
  API.get(API_ENDPOINTS.PAYMENT_REQUESTS, { params });

export const createPaymentRequest = (data) =>
  API.post(API_ENDPOINTS.PAYMENT_REQUESTS, data);


// --- Central Fund Ledger ---
export const fetchCentralFund = (params) =>
  API.get(API_ENDPOINTS.CENTRAL_FUND, { params });

export const fetchCentralFundBalance = () =>
  API.get(API_ENDPOINTS.CENTRAL_FUND_BALANCE);

export const loadCentralFund = (data) =>
  API.post(API_ENDPOINTS.CENTRAL_FUND_LOAD, data);

export const fundBranch = (data) =>
  API.post(API_ENDPOINTS.CENTRAL_FUND_FUND_BRANCH, data);

export const fetchCashSummary = () =>
  API.get(`${API_ENDPOINTS.CENTRAL_FUND}/cash-summary`);

// --- Day Status ---
export const fetchDayStatus = (params) =>
  API.get(API_ENDPOINTS.DAY_STATUS, { params });

// --- EOD Closure ---
export const initiateEod = (data) =>
  API.post(API_ENDPOINTS.EOD_INITIATE, data);

export const submitEodDenomination = (data) =>
  API.post(API_ENDPOINTS.EOD_DENOMINATION, data);

export const submitEodForApproval = (data) =>
  API.post(API_ENDPOINTS.EOD_SUBMIT, data);

// Unclosed previous days blocking today + whether current user may override
export const fetchBlockingDays = (branchId) =>
  API.get(`${API_ENDPOINTS.EOD_BLOCKING_DAYS}/${branchId}`);

// Authorized override user closes a stuck previous day directly
export const forceCloseDay = (data) =>
  API.post(API_ENDPOINTS.EOD_FORCE_CLOSE, data);

// Close ALL unclosed previous days at once (earlier days auto-balance;
// latest day uses the supplied physical count and holds the difference)
export const forceCloseAllDays = (data) =>
  API.post(API_ENDPOINTS.EOD_FORCE_CLOSE_ALL, data);

// --- Closure Approvals ---
export const fetchClosureApprovals = (params) =>
  API.get(API_ENDPOINTS.CLOSURE_APPROVALS, { params });

export const approveClosureRequest = (id) =>
  API.patch(`${API_ENDPOINTS.CLOSURE_APPROVALS}/${id}/approve`);

export const rejectClosureRequest = (id, data) =>
  API.patch(`${API_ENDPOINTS.CLOSURE_APPROVALS}/${id}/reject`, data);

export const reopenClosedDay = (id, data) =>
  API.patch(`${API_ENDPOINTS.CLOSURE_APPROVALS}/${id}/reopen`, data);

// --- Treasury Settings ---
export const fetchTreasurySettings = () =>
  API.get('/treasury/settings');

export const updateTreasurySettings = (data) =>
  API.patch('/treasury/settings', data);

// --- Branch Operation Overrides ---
export const fetchBranchOverrides = () =>
  API.get(API_ENDPOINTS.BRANCH_OVERRIDES);

export const setBranchBypassDayClosure = (branchId, bypass) =>
  API.patch(`${API_ENDPOINTS.BRANCH_OVERRIDES}/${branchId}/bypass-day-closure`, { bypass });

export const setBranchBypassPastDateEod = (branchId, bypass) =>
  API.patch(`${API_ENDPOINTS.BRANCH_OVERRIDES}/${branchId}/bypass-past-date-eod`, { bypass });

export const setBranchBankAutoApprove = (branchId, autoApprove) =>
  API.patch(`${API_ENDPOINTS.BRANCH_OVERRIDES}/${branchId}/bank-auto-approve`, { autoApprove });

export const setBranchCashLimit = (branchId, limit) =>
  API.patch(`${API_ENDPOINTS.BRANCH_OVERRIDES}/${branchId}/cash-limit`, { limit });

export const setBranchPledgeCashLimit = (branchId, limit) =>
  API.patch(`${API_ENDPOINTS.BRANCH_OVERRIDES}/${branchId}/pledge-cash-limit`, { limit });

export const fetchBranchEodVisibility = (branchId) =>
  API.get(`${API_ENDPOINTS.BRANCH_OVERRIDES}/${branchId}/eod-visibility`);

export const setBranchHideExpectedClosingCash = (branchId, hide) =>
  API.patch(`${API_ENDPOINTS.BRANCH_OVERRIDES}/${branchId}/hide-expected-closing-cash`, { hide });

export const setBranchHideExpectedCash = (branchId, hide) =>
  API.patch(`${API_ENDPOINTS.BRANCH_OVERRIDES}/${branchId}/hide-expected-cash`, { hide });

export const setBranchHideDifference = (branchId, hide) =>
  API.patch(`${API_ENDPOINTS.BRANCH_OVERRIDES}/${branchId}/hide-difference`, { hide });

export const setBranchHideGstToggle = (branchId, hide) =>
  API.patch(`${API_ENDPOINTS.BRANCH_OVERRIDES}/${branchId}/hide-gst-toggle`, { hide });

export const setBranchHideProcessingFeeToggle = (branchId, hide) =>
  API.patch(`${API_ENDPOINTS.BRANCH_OVERRIDES}/${branchId}/hide-processing-fee-toggle`, { hide });

export const fetchBranchGstToggleVisibility = (branchId) =>
  API.get(`${API_ENDPOINTS.BRANCH_OVERRIDES}/${branchId}/gst-toggle-visibility`);

export const fetchBranchProcessingFeeToggleVisibility = (branchId) =>
  API.get(`${API_ENDPOINTS.BRANCH_OVERRIDES}/${branchId}/processing-fee-toggle-visibility`);

// --- Inter-Branch Cash Transfer (IBCT) ---
export const fetchTransfers = (params) =>
  API.get(API_ENDPOINTS.TRANSFERS, { params });

export const fetchTransferSummary = (params) =>
  API.get(`${API_ENDPOINTS.TRANSFERS}/summary`, { params });

export const createTransfer = (data) =>
  API.post(API_ENDPOINTS.TRANSFERS, data);

export const confirmTransfer = (id) =>
  API.patch(`${API_ENDPOINTS.TRANSFERS}/${id}/confirm`);

export const rejectTransfer = (id, data) =>
  API.patch(`${API_ENDPOINTS.TRANSFERS}/${id}/reject`, data);

export const cancelTransfer = (id, data) =>
  API.patch(`${API_ENDPOINTS.TRANSFERS}/${id}/cancel`, data);

// --- HO Dashboard ---
export const fetchHoDashboardSummary = (params) =>
  API.get(API_ENDPOINTS.HO_DASHBOARD_SUMMARY, { params });

export const fetchHoDashboardBranches = (params) =>
  API.get(API_ENDPOINTS.HO_DASHBOARD_BRANCHES, { params });

export const fetchHoDashboardTrend = (params) =>
  API.get('/treasury/ho-dashboard/trend', { params });
